<template>
  <div class="landing-page">
    <!-- 背景层 -->
    <div class="bg-layer">
      <div class="bg-image"></div>
      <div class="bg-gradient"></div>
      <Particles />
    </div>

    <!-- 导航栏 -->
    <LandingNavbar />

    <!-- Hero 区域 -->
    <section class="hero">
      <div class="hero-content">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          {{ $t('landing.hero.badge') }}
        </div>
        <h1 class="hero-title">
          {{ $t('landing.hero.title') }}
          <span class="gradient-text">{{ $t('landing.hero.gradientText') }}</span>
        </h1>
        <p class="hero-desc">
          {{ $t('landing.hero.desc') }}
        </p>
        <div class="hero-actions">
          <button @click="goTranslate" class="btn btn-primary">
            <i class="fas fa-rocket"></i>
            {{ $t('landing.hero.startBtn') }}
          </button>
          <a href="#features" class="btn btn-outline">
            <i class="fas fa-play-circle"></i>
            {{ $t('landing.hero.learnMore') }}
          </a>
        </div>
        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-num">10K+</span>
            <span class="stat-label">{{ $t('landing.hero.statsTranslated') }}</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-num">{{ $t('landing.hero.statsFreeNum') }}</span>
            <span class="stat-label">{{ $t('landing.hero.statsFree') }}</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-num">99%</span>
            <span class="stat-label">{{ $t('landing.hero.statsAccuracy') }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 整体优势（左图右文） -->
    <section class="advantage-section" id="features">
      <div class="section-container">
        <div class="advantage-layout">
          <!-- 左侧：对比滑块 -->
          <div class="advantage-left">
            <div class="comparison-slider" ref="sliderRef"
              @mousedown="startDrag" @mousemove="onDrag" @mouseup="endDrag" @mouseleave="endDrag"
              @touchstart.passive="startDrag" @touchmove.passive="onDrag" @touchend="endDrag">
              <div class="comparison-wrapper">
                <div class="comparison-after">
                  <img :src="afterImg" :alt="$t('landing.comparison.afterLabel')" draggable="false" />
                  <span class="comparison-label after-label">
                    <i class="fas fa-language"></i> {{ $t('landing.comparison.afterLabel') }}
                  </span>
                </div>
                <div class="comparison-before" :style="{ clipPath: `inset(0 0 0 ${sliderPos}%)` }">
                  <img :src="beforeImg" :alt="$t('landing.comparison.beforeLabel')" draggable="false" />
                  <span class="comparison-label before-label">
                    <i class="fas fa-file-image"></i> {{ $t('landing.comparison.beforeLabel') }}
                  </span>
                </div>
                <div class="slider-handle" :style="{ left: sliderPos + '%' }" @mousedown.stop="startDrag">
                  <div class="slider-line"></div>
                  <div class="slider-button">
                    <i class="fas fa-arrows-left-right"></i>
                  </div>
                  <div class="slider-line"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- 右侧：优势列表 -->
          <div class="advantage-right">
            <span class="section-tag">{{ $t('landing.advantage.tag') }}</span>
            <h2 class="section-title">{{ $t('landing.advantage.title') }}</h2>
            <p class="section-desc">{{ $t('landing.advantage.desc') }}</p>

            <div class="features-list">
              <div v-for="(feature, i) in featuresList" :key="i" class="feature-item">
                <div class="feature-icon" :style="{ background: feature.gradient }">
                  <i :class="feature.icon"></i>
                </div>
                <div class="feature-text">
                  <h3 class="feature-title">{{ feature.title }}</h3>
                  <p class="feature-desc" v-html="feature.desc"></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 定价 -->
    <section class="pricing-section" id="pricing">
      <div class="section-container">
        <div class="section-header">
          <span class="section-tag">{{ $t('landing.pricing.tag') }}</span>
          <h2 class="section-title">{{ $t('landing.pricing.title') }}</h2>
          <p class="section-desc">{{ $t('landing.pricing.desc') }}</p>
        </div>

        <!-- 算力套餐卡片 -->
        <div class="packages-grid">
          <div class="package-card" v-for="(pkg, i) in $tm('landing.pricing.packages')" :key="i">
            <div v-if="pkg.badge" class="popular-badge">{{ pkg.badge }}</div>
            <div class="package-amount">{{ pkg.amount }}</div>
            <div class="package-price">{{ pkg.price.replace('¥', priceSymbol) }}</div>
            <div class="package-desc">{{ pkg.desc }}</div>
          </div>
        </div>

        <div class="pricing-note">
          <i class="fas fa-exclamation-circle"></i>
          {{ $t('landing.pricing.note') }}
        </div>

        <!-- 通道对比 -->
        <div class="comparison-section">
          <h3 class="comparison-title"><i class="fas fa-road"></i> {{ $t('landing.channelCompare.title') }}</h3>
          <div class="comparison-grid">
            <div class="queue-type free">
              <h4 class="queue-title free"><i class="fas fa-users"></i> {{ $t('landing.channelCompare.free.title') }}</h4>
              <p class="queue-desc">{{ $t('landing.channelCompare.free.desc') }}</p>
              <ul class="feature-list">
                <li v-for="(item, i) in $tm('landing.channelCompare.free.items')" :key="i">
                  <i :class="i < 2 ? 'fas fa-check' : 'fas fa-times'"></i> {{ item }}
                </li>
              </ul>
            </div>
            <div class="queue-type vip">
              <h4 class="queue-title vip"><i class="fas fa-crown"></i> {{ $t('landing.channelCompare.vip.title') }}</h4>
              <p class="queue-desc">{{ $t('landing.channelCompare.vip.desc') }}</p>
              <ul class="feature-list">
                <li v-for="(item, i) in $tm('landing.channelCompare.vip.items')" :key="i">
                  <i class="fas fa-check"></i> {{ item }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq-section" id="faq">
      <div class="section-container">
        <div class="section-header">
          <span class="section-tag">{{ $t('landing.faq.tag') }}</span>
          <h2 class="section-title">{{ $t('landing.faq.title') }}</h2>
          <p class="section-desc">{{ $t('landing.faq.desc') }}</p>
        </div>

        <div class="faq-list">
          <div v-for="(item, i) in $tm('landing.faq.items')" :key="i" class="faq-item" :class="{ active: activeFaq === i }">
            <div class="faq-question" @click="toggleFaq(i)">
              <span>{{ item.q }}</span>
              <i class="fas fa-chevron-down"></i>
            </div>
            <div class="faq-answer" :style="{ maxHeight: activeFaq === i ? '200px' : '0' }">
              <p>{{ item.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 快速开始 CTA -->
    <section class="cta-section">
      <div class="section-container">
        <div class="cta-card">
          <div class="cta-glow"></div>
          <h2 class="cta-title">{{ $t('landing.cta.title') }}</h2>
          <p class="cta-desc">{{ $t('landing.cta.desc') }}</p>
          <button @click="goTranslate" class="btn btn-primary btn-lg">
            <i class="fas fa-arrow-right"></i>
            {{ $t('landing.cta.btn') }}
          </button>
        </div>
      </div>
    </section>

    <!-- 页脚 -->
    <footer class="landing-footer">
      <div class="footer-content">
        <div class="footer-brand">
          <img src="@/assets/logo.png" alt="Koke Logo" class="footer-logo" />
          <span class="footer-name">KokeManga</span>
        </div>
        <div class="footer-links">
          <a href="#features">{{ $t('landing.footer.features') }}</a>
          <a href="#pricing">{{ $t('landing.footer.pricing') }}</a>
          <a href="#faq">{{ $t('landing.footer.faq') }}</a>
          <router-link :to="localPath('/guide')">{{ $t('seoPage.guide.h1') }}</router-link>
          <router-link :to="localPath('/faq')">{{ $t('seoPage.faq.h1') }}</router-link>
          <router-link :to="localPath('/privacy')">{{ $t('manga.footer.privacy') }}</router-link>
          <router-link :to="localPath('/login')">{{ $t('landing.footer.login') }}</router-link>
        </div>
        <div class="footer-copyright">
          © {{ new Date().getFullYear() }} KokeManga. All rights reserved.
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { localPath } from '@/i18n'
import { priceSymbol } from '@/utils/config.js'
import { getToken } from '@/utils/auth.js'
import LandingNavbar from './LandingNavbar.vue'
import Particles from '@/components/Particles.vue'
import beforeImg from '@/assets/images/yl-a.jpg'
import afterImg from '@/assets/images/yl-b.jpg'

const router = useRouter()
const { t, tm } = useI18n()

// 登录状态
const isLoggedIn = computed(() => {
  const token = getToken()
  return !!(token && token !== undefined)
})

// 根据登录状态跳转
const goTranslate = () => {
  window.scrollTo(0, 0)
  if (isLoggedIn.value) {
    router.push(localPath('/vip'))
  } else {
    router.push(localPath('/MangaTranslation'))
  }
}

// SEO

// ========== 对比滑块 ==========
const sliderRef = ref(null)
const sliderPos = ref(50)
const isDragging = ref(false)

const startDrag = (e) => {
  isDragging.value = true
  updateSlider(e)
}

const onDrag = (e) => {
  if (!isDragging.value) return
  updateSlider(e)
}

const endDrag = () => {
  isDragging.value = false
}

const updateSlider = (e) => {
  if (!sliderRef.value) return
  const rect = sliderRef.value.getBoundingClientRect()
  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  let pos = ((clientX - rect.left) / rect.width) * 100
  pos = Math.max(0, Math.min(100, pos))
  sliderPos.value = pos
}

onMounted(() => {
  document.addEventListener('mousemove', onDrag)
  document.addEventListener('mouseup', endDrag)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onDrag)
  document.removeEventListener('mouseup', endDrag)
})

// ========== Features (handled by i18n) ==========
const featureIcons = [
  { icon: 'fas fa-robot', gradient: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))' },
  { icon: 'fas fa-bolt', gradient: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))' },
  { icon: 'fas fa-layer-group', gradient: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))' },
  { icon: 'fas fa-globe', gradient: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))' },
  { icon: 'fas fa-shield-alt', gradient: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))' },
  { icon: 'fas fa-file-download', gradient: 'linear-gradient(135deg, rgba(94,234,212,0.15), rgba(94,234,212,0.05))' }
]

const featuresList = computed(() => {
  const items = tm('landing.advantage.items')
  return items.map((item, i) => ({
    ...item,
    icon: featureIcons[i]?.icon || '',
    gradient: featureIcons[i]?.gradient || ''
  }))
})

// ========== FAQ ==========
const activeFaq = ref(null)
const toggleFaq = (i) => {
  activeFaq.value = activeFaq.value === i ? null : i
}
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

/* ========== 全局重置 ========== */
.landing-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  color: #e2e8f0;
}

/* ========== 背景层 ========== */
.bg-layer {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  z-index: 0;
}

.bg-image {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: url('~@/assets/images/login.jpg') no-repeat center center;
  background-size: cover;
}

.bg-gradient {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: linear-gradient(135deg, rgba(10, 15, 26, 0.94) 0%, rgba(15, 23, 42, 0.9) 50%, rgba(30, 41, 59, 0.88) 100%);
}

/* ========== Hero ========== */
.hero {
  position: relative;
  z-index: 1;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5%;
  text-align: center;
  box-sizing: border-box;
}

.hero-content {
  max-width: 800px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.2);
  border-radius: 50px;
  font-size: 0.85rem;
  color: #5eead4;
  margin-bottom: 2rem;
  animation: fadeInUp 0.8s ease;
}

.badge-dot {
  width: 8px; height: 8px;
  background: #5eead4;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.5); }
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.8s ease 0.1s both;
}

.gradient-text {
  display: block;
  background: linear-gradient(90deg, #5eead4, #a78bfa, #f472b6);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  animation: gradientShift 4s ease infinite;
}

@keyframes gradientShift {
  0%, 100% { background-position: 0% center; }
  50% { background-position: 200% center; }
}

.hero-desc {
  font-size: 1.2rem;
  color: #94a3b8;
  margin-bottom: 2.5rem;
  animation: fadeInUp 0.8s ease 0.2s both;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  animation: fadeInUp 0.8s ease 0.3s both;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s;
  cursor: pointer;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, #5eead4, #06b6d4);
  color: #0a0f1a;
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(94, 234, 212, 0.3);
}

.btn-outline {
  background: transparent;
  border: 1px solid rgba(94, 234, 212, 0.3);
  color: #5eead4;
}

.btn-outline:hover {
  background: rgba(94, 234, 212, 0.1);
  border-color: #5eead4;
}

.btn-lg {
  padding: 18px 48px;
  font-size: 1.1rem;
}

.hero-stats {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  animation: fadeInUp 0.8s ease 0.4s both;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-num {
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.stat-label {
  font-size: 0.85rem;
  color: #64748b;
  margin-top: 4px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(94, 234, 212, 0.2);
}

/* ========== 通用 Section ========== */
.section-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 5%;
}

.section-header {
  text-align: center;
  margin-bottom: 4rem;
}

.section-tag {
  display: inline-block;
  padding: 6px 16px;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 50px;
  font-size: 0.8rem;
  color: #5eead4;
  margin-bottom: 1rem;
  letter-spacing: 1px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #f1f5f9, #94a3b8);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.section-desc {
  font-size: 1.1rem;
  color: #64748b;
  max-width: 600px;
  margin: 0 auto;
}

/* ========== 整体优势（左右布局） ========== */
.advantage-section {
  position: relative;
  z-index: 1;
  padding: 6rem 0;
}

.advantage-layout {
  display: flex;
  align-items: center;
  gap: 4rem;
}

.advantage-left {
  flex: 1;
  min-width: 0;
}

.advantage-right {
  flex: 1;
  min-width: 0;
}

.advantage-right .section-tag {
  display: inline-block;
  padding: 6px 16px;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 50px;
  font-size: 0.8rem;
  color: #5eead4;
  margin-bottom: 1rem;
  letter-spacing: 1px;
}

.advantage-right .section-title {
  font-size: 2.2rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #f1f5f9, #94a3b8);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.advantage-right .section-desc {
  font-size: 1rem;
  color: #64748b;
  margin: 0 0 2rem 0;
  max-width: none;
  line-height: 1.6;
}

/* 对比滑块 */
.comparison-slider {
  cursor: ew-resize;
  user-select: none;
  -webkit-user-select: none;
}

.comparison-wrapper {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(94, 234, 212, 0.15);
}

.comparison-after,
.comparison-before {
  position: relative;
  width: 100%;
}

.comparison-after img,
.comparison-before img {
  width: 100%;
  display: block;
  pointer-events: none;
}

.comparison-before {
  position: absolute;
  top: 0; left: 0;
  width: 100%; height: 100%;
}

.comparison-label {
  position: absolute;
  bottom: 12px;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 5px;
  backdrop-filter: blur(10px);
}

.before-label {
  left: 12px;
  background: rgba(251, 191, 36, 0.2);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fbbf24;
}

.after-label {
  right: 12px;
  background: rgba(94, 234, 212, 0.2);
  border: 1px solid rgba(94, 234, 212, 0.3);
  color: #5eead4;
}

.slider-handle {
  position: absolute;
  top: 0;
  height: 100%;
  width: 4px;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.slider-line {
  flex: 1;
  width: 2px;
  background: linear-gradient(to bottom, transparent, #5eead4, transparent);
}

.slider-button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #5eead4, #06b6d4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0a0f1a;
  font-size: 0.9rem;
  box-shadow: 0 0 20px rgba(94, 234, 212, 0.4);
  transition: transform 0.2s;
  flex-shrink: 0;
  animation: sliderPulse 2s ease infinite;
}

@keyframes sliderPulse {
  0%, 100% { box-shadow: 0 0 20px rgba(94, 234, 212, 0.4); }
  50% { box-shadow: 0 0 30px rgba(94, 234, 212, 0.7); }
}

.slider-button:hover {
  transform: scale(1.1);
}

/* 优势列表 */
.features-list {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.2rem;
  background: rgba(30, 41, 59, 0.4);
  border: 1px solid rgba(94, 234, 212, 0.06);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.feature-item:hover {
  border-color: rgba(94, 234, 212, 0.15);
  background: rgba(30, 41, 59, 0.6);
}

.feature-item .feature-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  color: #5eead4;
  background: rgba(94, 234, 212, 0.1);
  border: 1px solid rgba(94, 234, 212, 0.2);
  flex-shrink: 0;
  margin: 0;
}

.feature-item .feature-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  padding: 0;
}

.feature-item .feature-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #f1f5f9;
}

.feature-item .feature-desc {
  font-size: 0.85rem;
  color: #94a3b8;
  line-height: 1.5;
  margin: 0;
}

.feature-item .feature-desc :deep(.highlight) {
  color: #5eead4;
  font-weight: 600;
}

/* ========== 定价 ========== */
.pricing-section {
  position: relative;
  z-index: 1;
  padding: 6rem 0;
}

/* 算力套餐网格 */
.packages-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.package-card {
  position: relative;
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(94, 234, 212, 0.1);
  border-radius: 16px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;
}

.package-card:hover {
  transform: translateY(-5px);
  border-color: rgba(94, 234, 212, 0.3);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.popular-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #f87171, #ef4444);
  color: white;
  padding: 4px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
}

.package-amount {
  font-size: 1.4rem;
  font-weight: 700;
  color: #5eead4;
  margin-bottom: 8px;
}

.package-price {
  font-size: 1.5rem;
  font-weight: 800;
  color: #fbbf24;
  margin-bottom: 6px;
}

.package-desc {
  font-size: 0.8rem;
  color: #94a3b8;
  line-height: 1.4;
}

.package-empty {
  grid-column: 1 / -1;
  text-align: center;
  padding: 2rem;
  color: #64748b;
}

.pricing-note {
  text-align: center;
  color: #64748b;
  font-size: 0.85rem;
  margin-bottom: 3rem;
  padding: 12px 20px;
  background: rgba(30, 41, 59, 0.3);
  border-radius: 8px;
}

.pricing-note i {
  color: #5eead4;
  margin-right: 6px;
}

/* 通道对比 */
.comparison-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #f1f5f9;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.comparison-title i {
  color: #5eead4;
}

.comparison-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.queue-type {
  background: rgba(30, 41, 59, 0.5);
  border-radius: 16px;
  padding: 1.5rem;
}

.queue-type.free {
  border: 1px solid rgba(148, 163, 184, 0.2);
}

.queue-type.vip {
  border: 1px solid rgba(167, 139, 250, 0.3);
  background: linear-gradient(135deg, rgba(167, 139, 250, 0.08), rgba(94, 234, 212, 0.03));
}

.queue-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.queue-title.free {
  color: #94a3b8;
}

.queue-title.vip {
  color: #a78bfa;
}

.queue-desc {
  color: #64748b;
  font-size: 0.85rem;
  margin-bottom: 12px;
}

.feature-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  font-size: 0.9rem;
  color: #cbd5e1;
}

.feature-list li i {
  width: 16px;
  font-size: 0.8rem;
}

.feature-list li i.fa-check {
  color: #5eead4;
}

.feature-list li i.fa-times {
  color: #f87171;
}

/* ========== FAQ ========== */
.faq-section {
  position: relative;
  z-index: 1;
  padding: 6rem 0;
}

.faq-list {
  max-width: 700px;
  margin: 0 auto;
}

.faq-item {
  background: rgba(30, 41, 59, 0.5);
  border: 1px solid rgba(94, 234, 212, 0.08);
  border-radius: 16px;
  margin-bottom: 1rem;
  overflow: hidden;
  transition: all 0.3s;
}

.faq-item:hover {
  border-color: rgba(94, 234, 212, 0.15);
}

.faq-item.active {
  border-color: rgba(94, 234, 212, 0.2);
  background: rgba(30, 41, 59, 0.7);
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 1.5rem;
  cursor: pointer;
  font-weight: 600;
  color: #e2e8f0;
  transition: color 0.3s;
}

.faq-question:hover {
  color: #5eead4;
}

.faq-question i {
  transition: transform 0.3s;
  color: #5eead4;
  font-size: 0.85rem;
}

.faq-item.active .faq-question i {
  transform: rotate(180deg);
}

.faq-answer {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.faq-answer p {
  padding: 0 1.5rem 1.2rem;
  color: #94a3b8;
  font-size: 0.95rem;
  line-height: 1.7;
}

/* ========== CTA ========== */
.cta-section {
  position: relative;
  z-index: 1;
  padding: 6rem 0;
}

.cta-card {
  position: relative;
  text-align: center;
  padding: 5rem 2rem;
  background: rgba(30, 41, 59, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(94, 234, 212, 0.15);
  border-radius: 32px;
  overflow: hidden;
}

.cta-glow {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(94, 234, 212, 0.15), transparent 70%);
  pointer-events: none;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: #f1f5f9;
  position: relative;
}

.cta-desc {
  font-size: 1.1rem;
  color: #94a3b8;
  margin-bottom: 2rem;
  position: relative;
}

/* ========== 页脚 ========== */
.landing-footer {
  position: relative;
  z-index: 1;
  padding: 3rem 5%;
  border-top: 1px solid rgba(94, 234, 212, 0.08);
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.footer-logo {
  height: 1.8rem;
}

.footer-name {
  font-size: 1.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #5eead4, #a78bfa);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.footer-links {
  display: flex;
  gap: 2rem;
}

.footer-links a {
  color: #64748b;
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: #5eead4;
}

.footer-copyright {
  color: #475569;
  font-size: 0.8rem;
}

/* ========== 动画 ========== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== 响应式 ========== */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2.5rem;
  }

  .hero-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .stat-divider {
    width: 40px;
    height: 1px;
  }

  .advantage-layout {
    flex-direction: column;
    gap: 2rem;
  }

  .advantage-right .section-title {
    font-size: 1.8rem;
  }

  .packages-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .comparison-grid {
    grid-template-columns: 1fr;
  }

  .formula-examples {
    grid-template-columns: 1fr;
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .nav-links {
    display: none;
  }

  .section-title {
    font-size: 1.8rem;
  }
}
</style>
